import { useContext, useState } from 'react'
import { useTranslation } from 'libs/i18n'
import { FieldErrors, FieldValues, useForm } from 'libs/form'
import { useSearchParams } from 'libs/router'
import { ConfirmModal, Header, PageTabs, Form } from 'shared/components'
import { useFetchPayoutMethods, usePayoutMethodLabels } from 'shared/hooks'
import { StoreSettingsContext } from 'store/StoreSettingsContext'
import { useFetchCountries } from 'shared/queries/useFetchCountries'
import { useFetchCurrencies } from 'shared/queries/useFetchCurrencies'
import { useSettingsValidationSchema } from './hooks/useSettingsValidationSchema'
import { useTabs } from './hooks/useTabs'
import { DEFAULT_COLOR } from './config/defaults'
import { useChangePrimaryColorMutation } from './mutations/useChangePrimaryColorMutation'
import { useUpdateStoreMutation } from './mutations/useUpdateStoreMutation'
import { useFetchLanguages } from './queries/useFetchLanguages'
import { useSetCountry } from './utils/useSetCountry'
import { usePopulateSettings } from './hooks/usePopulateSettings'
import { useSetCurrency } from './hooks/useSetCurrency'
import { useSetDefaultLanguage } from './hooks/useSetDefaultLanguage'
import { useMapDataToFormValues } from './hooks/useMapDataToFormValues'
import { AccountFields } from './organisms/Account/AccountFields'
import { Commissions } from './organisms/Commissions/Commissions'
import { LegalFields } from './organisms/Legal/LegalFields'
import { PaymentsFields } from './organisms/Payments/PaymentsFields'
import { ProductRegistrationFields } from './organisms/ProductRegistration/ProductRegistrationFields'
import { SocialFields } from './organisms/Social/SocialFields'

const TAB_FIELDS: Record<string, string[]> = {
	account: ['name', 'address', 'country'],
	legal: ['legal_address', 'legal_country', 'terms_of_service_url'],
	payments: [
		'start_reminder_sms_template',
		'end_reminder_sms_template',
		'booking_fees',
		'enabled_payout_methods'
	]
}

const SettingsPage = () => {
	const { t } = useTranslation()
	const tabs = useTabs()
	const storeCtx = useContext(StoreSettingsContext)
	const [accentColor, setAccentColor] = useState(DEFAULT_COLOR)
	const [searchParams, setSearchParams] = useSearchParams()
	const [pendingData, setPendingData] = useState<FieldValues | null>(null)
	const activeTab = tabs.find((tab) => tab.value === searchParams.get('tab')) ?? tabs[0]
	const validationResolver = useSettingsValidationSchema()

	const form = useForm({
		mode: 'onChange',
		resolver: validationResolver
	})
	const { setValue, setError, handleSubmit } = form

	const { countries, isCountriesLoading } = useFetchCountries()
	const { languages, languagesIsLoading } = useFetchLanguages()
	const { currencies, currenciesIsLoading } = useFetchCurrencies()
	const { payoutMethods } = useFetchPayoutMethods()
	const payoutMethodLabels = usePayoutMethodLabels()
	const mapDataToFormValues = useMapDataToFormValues()

	usePopulateSettings({
		setValue,
		setAccentColor
	})

	useSetCountry({
		countries,
		setValue
	})

	useSetCurrency({
		currencies,
		setValue
	})

	useSetDefaultLanguage({
		languages,
		setValue
	})

	const { updateStoreMutation, isUpdateStoreLoading } = useUpdateStoreMutation(setError)
	const { changePrimaryColorMutation } = useChangePrimaryColorMutation()

	const showTabWithErrors = (errors: FieldErrors) => {
		const tabWithErrors = tabs.find((tab) =>
			TAB_FIELDS[tab.value]?.some((field) => field in errors)
		)

		if (tabWithErrors && tabWithErrors.value !== activeTab.value) {
			setSearchParams({ tab: tabWithErrors.value })
		}
	}

	const getSwitchedOffPayoutMethods = (data: FieldValues) =>
		Array.isArray(data.enabled_payout_methods)
			? payoutMethods.filter((method) => !data.enabled_payout_methods.includes(method.kind))
			: []

	const submitSettings = (data: FieldValues) => {
		const formData = mapDataToFormValues.mapData(data)

		updateStoreMutation(formData)
		if (accentColor !== storeCtx?.primaryColor) {
			changePrimaryColorMutation({ name: data.name, primary_color: accentColor })
		}
	}

	const onSubmit = handleSubmit((data) => {
		if (getSwitchedOffPayoutMethods(data).length) {
			setPendingData(data)
			return
		}

		submitSettings(data)
	}, showTabWithErrors)

	const confirmPendingSubmit = () => {
		if (pendingData) submitSettings(pendingData)
		setPendingData(null)
	}

	const renderFields = () => {
		switch (activeTab.value) {
			case 'account':
				return (
					<AccountFields
						form={form}
						accentColor={accentColor}
						countries={countries}
						isCountriesLoading={isCountriesLoading}
						languages={languages}
						isLanguagesLoading={languagesIsLoading}
						onAccentColorChange={setAccentColor}
					/>
				)
			case 'legal':
				return (
					<LegalFields form={form} countries={countries} isCountriesLoading={isCountriesLoading} />
				)
			case 'social':
				return <SocialFields form={form} />
			case 'payments':
				return (
					<PaymentsFields
						form={form}
						currencies={currencies}
						isCurrenciesLoading={currenciesIsLoading}
					/>
				)
			case 'productRegistration':
				return <ProductRegistrationFields form={form} />
			default:
				return null
		}
	}

	return (
		<>
			<Header
				title={activeTab.label}
				eyebrow={t('Settings')}
				tabs={
					<PageTabs
						tabs={tabs}
						activeTab={activeTab.value}
						label={t('Settings')}
						onChange={(value) => setSearchParams({ tab: value })}
					/>
				}
			/>
			<div className="mx-auto max-w-2xl px-5 lg:px-0">
				{activeTab.value === 'commissions' ? (
					<Commissions />
				) : (
					<Form
						variant="card"
						submitText={t('Save')}
						onSubmit={onSubmit}
						isLoading={isUpdateStoreLoading}>
						{renderFields()}
					</Form>
				)}
			</div>
			{pendingData && (
				<ConfirmModal
					isOpened
					tone="danger"
					title={t('Switch off payout method')}
					description={t(
						'Switching off {{ methods }} clears the payout method of every consignor currently using it.',
						{
							methods: getSwitchedOffPayoutMethods(pendingData)
								.map((method) => payoutMethodLabels[method.kind])
								.join(', ')
						}
					)}
					confirmText={t('Switch off and save')}
					closeModal={() => setPendingData(null)}
					confirm={confirmPendingSubmit}
				/>
			)}
		</>
	)
}

export default SettingsPage
